import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Input, notification, Spin, Pagination } from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Loader2, Save, Wrench } from 'lucide-react';
import moment from 'moment';
import { connect } from 'react-redux';
import { fetchEquipmentUsageLog, updateEquipmentUsageLog, updateDsrLogSections } from '../../redux/dsr/action';
import TechnicianLogCustomSelect from './TechnicianLogCustomSelect';
import './TechnicianLogSection.css';
import './EquipmentUsageSection.css';

const { TextArea } = Input;

const STATUS_SELECT_OPTIONS = ['Select Status', 'OFFLINE', 'BACKUP', 'ONLINE'];
const SPARE_SELECT_OPTIONS = ['Select', 'YES', 'NO'];

const EquipmentUsageSection = ({
  shift,
  selectedDate,
  selectedProject,
  selectedMainline,
  currentDsrLog,
  isSkipped,
  isSubmitted,
  onComplete,
  onCancel,
  isShiftSkipped = false,
  onSkipChange,
  onFetchEquipmentUsageLog,
  onUpdateEquipmentUsageLog,
  onUpdateDsrLogSections,
  isEquipmentUsageLogLoading,
  isUpdatingEquipmentUsageLog,
  currentEquipmentUsageLog,
}) => {
  const [rowData, setRowData] = useState([]);
  const [comments, setComments] = useState('');
  const [skipped, setSkipped] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [paginationState, setPaginationState] = useState({ current: 1, pageSize: 10 });
  const [hoveredRowId, setHoveredRowId] = useState(null);

  const getShiftNumber = (shiftStr) => {
    return shiftStr === 'day' ? 0 : 1;
  };

  const getStatusFromNumber = (status) => {
    switch (status) {
      case 1: return 'OFFLINE';
      case 2: return 'BACKUP';
      case 3: return 'ONLINE';
      default: return 'Select Status';
    }
  };

  const getStatusToNumber = (status) => {
    switch (status) {
      case 'OFFLINE': return 1;
      case 'BACKUP': return 2;
      case 'ONLINE': return 3;
      default: return 'Select Status';
    }
  };

  const getSpareAvailableFromNumber = (spareAvailable) => {
    switch (spareAvailable) {
      case 1: return 'YES';
      case 2: return 'NO';
      default: return 'Select';
    }
  };

  const getSpareAvailableToNumber = (spareAvailable) => {
    switch (spareAvailable) {
      case 'YES': return 1;
      case 'NO': return 2;
      default: return null;
    }
  };

  const convertApiDataToTableFormat = (apiData) => {
    if (!apiData || !apiData.data || !apiData.data.data) {
      return [];
    }

    const convertedData = apiData.data.data.map((item) => ({
      id: item.id,
      pihEquipmentId: item.pihEquipmentId,
      equipment: item.pihEquipmentType || item.equipmentName || '-',
      serialNumber: item.serialNumber || '-',
      status: getStatusFromNumber(item.status),
      hsCode: item.hsCode || '',
      spareAvail: getSpareAvailableFromNumber(item.spareAvailable),
      type: item.type || '',
      issues: item.issues || '',
      apiData: item,
    }));

    return convertedData;
  };

  useEffect(() => {
    if (selectedProject && selectedMainline && selectedDate) {
      const userEmail = JSON.parse(localStorage.getItem('user')).email;
      const logDate = moment(selectedDate).format('YYYY-MM-DD');
      const shiftNumber = getShiftNumber(shift);

      onFetchEquipmentUsageLog({
        projectId: selectedProject.id,
        logDate: logDate,
        userEmail: userEmail,
        shift: shiftNumber,
        sowId: selectedMainline.id,
      });
    }
  }, [selectedProject, selectedMainline, selectedDate, shift, onFetchEquipmentUsageLog]);

  useEffect(() => {
    if (currentEquipmentUsageLog) {
      const convertedData = convertApiDataToTableFormat(currentEquipmentUsageLog);
      setRowData(convertedData);
      setIsDirty(false);
    }
  }, [currentEquipmentUsageLog]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped]);

  useEffect(() => {
    setIsDirty(false);
  }, [selectedDate]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped, shift, selectedDate]);

  useEffect(() => {
    return () => {
      setRowData([]);
      setComments('');
      setSkipped(false);
      setIsDirty(false);
    };
  }, []);

  const updateRowField = useCallback((id, field, value) => {
    setIsDirty(true);
    setRowData((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  }, []);

  const totalRows = rowData.length;
  const { current: currentPage, pageSize } = paginationState;
  const maxPage = Math.max(1, totalRows === 0 ? 1 : Math.ceil(totalRows / pageSize));
  const page = Math.min(currentPage, maxPage);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rowData.slice(start, start + pageSize);
  }, [rowData, page, pageSize]);

  useEffect(() => {
    if (currentPage > maxPage) {
      setPaginationState((s) => ({ ...s, current: maxPage }));
    }
  }, [currentPage, maxPage]);

  const isCellDisabled = skipped || isSubmitted;

  const handleCommentsChange = (e) => {
    setComments(e.target.value);
    setIsDirty(true);
  };

  const handleSkipChange = (e) => {
    const newSkipState = e.target.checked;
    setSkipped(newSkipState);
    setIsDirty(true);

    if (onSkipChange) {
      onSkipChange(e);
    } else {
      if (currentDsrLog?.id) {
        const submissionStatus = generateSubmissionStatus('equipment', shift, true);
        const sectionsSkipped = {
          ...currentDsrLog.sectionsSkipped,
          isEquipmentUsageSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isEquipmentUsageSkippedDayShift || 0),
          isEquipmentUsageSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isEquipmentUsageSkippedNightShift || 0),
        };

        onUpdateDsrLogSections({
          dsrLogId: currentDsrLog.id,
          submissionStatus: submissionStatus,
          sectionsSkipped: sectionsSkipped,
        });
      }
    }
  };

  const generateSubmissionStatus = (section, sh, submittedFlag = true) => {
    const shiftKeySuffix = sh === 'day' ? 'Dayshift' : 'Nightshift';
    let submittedKey;

    switch (section) {
      case 'technician':
        submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
        break;
      case 'downtime':
        submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
        break;
      case 'consumables':
        submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
        break;
      case 'equipment':
        submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'ehs':
        submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
        break;
      case 'material':
        submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'production':
        submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
        break;
      default:
        return {};
    }

    return { [submittedKey]: submittedFlag };
  };

  const handleSave = async () => {
    if (skipped) {
      if (onComplete) onComplete(true, 'equipment');
      return;
    }

    try {
      const dsrLogEquipmentUsage = rowData.map((row) => ({
        hsCode: row.hsCode || null,
        status: getStatusToNumber(row.status),
        type: row.type || null,
        spareAvailable: getSpareAvailableToNumber(row.spareAvail),
        issues: row.issues || null,
        pihEquipmentId: row.pihEquipmentId,
        dsrLogId: currentDsrLog?.id,
        shift: getShiftNumber(shift),
      }));

      const payload = {
        dsrLogEquipmentUsage,
      };

      await onUpdateEquipmentUsageLog(payload);
      setIsDirty(false);

      const submissionStatus = generateSubmissionStatus('equipment', shift, true);
      const sectionsSkipped = {
        ...currentDsrLog.sectionsSkipped,
        isEquipmentUsageSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isEquipmentUsageSkippedDayShift || 0),
        isEquipmentUsageSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isEquipmentUsageSkippedNightShift || 0),
      };

      await onUpdateDsrLogSections({
        dsrLogId: currentDsrLog.id,
        submissionStatus: submissionStatus,
        sectionsSkipped: sectionsSkipped,
      });

      if (onComplete) onComplete(true, 'equipment');

      if (selectedProject && selectedMainline && selectedDate) {
        const userEmail = JSON.parse(localStorage.getItem('user')).email;
        const logDate = moment(selectedDate).format('YYYY-MM-DD');
        const shiftNumber = shift === 'day' ? '0' : '1';

        onFetchEquipmentUsageLog({
          projectId: selectedProject.id,
          logDate: logDate,
          userEmail: userEmail,
          shift: shiftNumber,
          sowId: selectedMainline.id,
        });
      }
    } catch (error) {
      console.error('Error saving equipment usage log:', error);
      notification.error({
        message: 'Save Failed',
        description: 'Failed to save equipment usage data. Please try again.',
      });
    }
  };

  return (
    <div className="modern-dsr-tech-log-section modern-dsr-eq-usage">
      {isShiftSkipped ? (
        <div className="modern-dsr-tech-log-skipped-banner">
          <strong>
            {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
          </strong>
          <p>
            This section is disabled because the {shift} shift has been skipped.
          </p>
        </div>
      ) : (
        <Spin
          spinning={isEquipmentUsageLogLoading}
          indicator={(
            <span className="modern-dsr-tech-log-spin" aria-hidden>
              <Loader2 className="modern-dsr-tech-log-spin-icon" size={28} strokeWidth={2} />
            </span>
          )}
          tip="Loading equipment usage data..."
          size="large"
        >
          <div className="modern-dsr-tech-log-card">
            <div className="modern-dsr-tech-log-card-head">
              <div className="modern-dsr-tech-log-card-title-block">
                <div className="modern-dsr-tech-log-card-icon" aria-hidden>
                  <Wrench size={22} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="modern-dsr-tech-log-card-title">Equipment usage</h3>
                  <p className="modern-dsr-tech-log-card-sub">Track equipment status, spares, and per-row notes for this shift.</p>
                </div>
              </div>
            </div>

            <div
              className="modern-dsr-tech-table"
              role="table"
              aria-label="Equipment usage"
            >
              <div
                className="modern-dsr-tech-thead modern-dsr-eq-grid"
                role="row"
              >
                <div className="modern-dsr-tech-th" role="columnheader">Equipment</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Serial number</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Status</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">HS code</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Spare avail. (Y/N)</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Type</div>
                <div className="modern-dsr-tech-th" role="columnheader">Issues</div>
              </div>

              {pagedRows.length === 0 ? (
                <div className="modern-dsr-tech-empty">No equipment loaded.</div>
              ) : (
                pagedRows.map((row) => (
                  <div
                    key={row.id}
                    className="modern-dsr-tech-row modern-dsr-eq-grid"
                    role="row"
                    onMouseEnter={() => setHoveredRowId(row.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    data-active={hoveredRowId === row.id ? 'true' : undefined}
                  >
                    <div className="modern-dsr-tech-td modern-dsr-tech-td--name" role="cell">
                      <span className="modern-dsr-tech-name-text" title={row.equipment}>
                        {row.equipment}
                      </span>
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <span className="modern-dsr-tech-name-text" style={{ fontWeight: 500 }} title={row.serialNumber}>
                        {row.serialNumber}
                      </span>
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <TechnicianLogCustomSelect
                        id={`dd-eq-status-${row.id}`}
                        value={row.status}
                        options={STATUS_SELECT_OPTIONS}
                        onChange={(v) => updateRowField(row.id, 'status', v)}
                        disabled={isCellDisabled}
                        placeholder="Select Status"
                        showSearch={false}
                      />
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <Input
                        className="modern-dsr-eq-text-input"
                        value={row.hsCode}
                        onChange={(e) => updateRowField(row.id, 'hsCode', e.target.value)}
                        disabled={isCellDisabled}
                        placeholder="HS code"
                      />
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <TechnicianLogCustomSelect
                        id={`dd-eq-spare-${row.id}`}
                        value={row.spareAvail || 'Select'}
                        options={SPARE_SELECT_OPTIONS}
                        onChange={(v) => updateRowField(row.id, 'spareAvail', v)}
                        disabled={isCellDisabled}
                        placeholder="Select"
                        showSearch={false}
                      />
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <Input
                        className="modern-dsr-eq-text-input"
                        value={row.type}
                        onChange={(e) => updateRowField(row.id, 'type', e.target.value)}
                        disabled={isCellDisabled}
                        placeholder="Type"
                      />
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <Input
                        className="modern-dsr-eq-text-input"
                        value={row.issues}
                        onChange={(e) => updateRowField(row.id, 'issues', e.target.value)}
                        disabled={isCellDisabled}
                        placeholder="Issues"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modern-dsr-tech-log-pagination-bar">
              <Pagination
                className="heat-profile-pagination modern-dsr-tech-heat-profile-pagination"
                current={page}
                pageSize={pageSize}
                total={totalRows}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                showTotal={(t, range) =>
                  t > 0 ? `${range[0]}-${range[1]} of ${t}` : `0-0 of ${t}`
                }
                onChange={(p, newSize) => {
                  setPaginationState((s) => {
                    if (newSize !== s.pageSize) {
                      return { current: 1, pageSize: newSize };
                    }
                    return { ...s, current: p };
                  });
                }}
              />
            </div>

            <div className="modern-dsr-tech-log-issues-block">
              <div className="modern-dsr-tech-log-issues-label">Comments</div>
              <TextArea
                className="modern-dsr-tech-log-issues-textarea"
                rows={3}
                placeholder="Please add your comments here"
                value={comments}
                onChange={handleCommentsChange}
                disabled={skipped || isSubmitted}
              />
            </div>
          </div>
        </Spin>
      )}

      <div className="modern-dsr-tech-log-form-footer" aria-label="Form actions">
        <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={onCancel} />
        <CrceButton
          label={isUpdatingEquipmentUsageLog ? 'Saving…' : 'Save & Next'}
          variant="primary"
          size="medium"
          icon={Save}
          onClick={handleSave}
          disabled={isUpdatingEquipmentUsageLog || isSubmitted || isShiftSkipped}
        />
      </div>
    </div>
  );
};

const mapStateToProps = ({ dsrReducer }) => ({
  isEquipmentUsageLogLoading: dsrReducer.isEquipmentUsageLogLoading,
  isUpdatingEquipmentUsageLog: dsrReducer.isUpdatingEquipmentUsageLog,
  currentEquipmentUsageLog: dsrReducer.currentEquipmentUsageLog,
});

const mapDispatchToProps = (dispatch) => ({
  onFetchEquipmentUsageLog: (payload) => dispatch(fetchEquipmentUsageLog(payload)),
  onUpdateEquipmentUsageLog: (payload) => dispatch(updateEquipmentUsageLog(payload)),
  onUpdateDsrLogSections: (payload) => dispatch(updateDsrLogSections(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EquipmentUsageSection);
